import { useQuery } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { cn } from '~/lib/cn';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/modules/label-perf/components/Table';
import { SkeletonTable } from '~/modules/label-perf/components/Table/SkeletonTable';

import { getDataFreshness } from '../../api/dataFreshness';
import { ErrorDisplay } from '../LabelPerf/components';
import { useDataFreshnessTableColumns } from './useDataFreshnessTableColumns';

function DataFreshness() {
  const { t } = useTranslation('label-perf');

  const { data, isError, isFetching } = useQuery({
    queryKey: ['data-freshness'],
    queryFn: () => getDataFreshness(),
  });

  const columns = useDataFreshnessTableColumns();
  const tableData = data?.items ?? [];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isError) {
    return (
      <ErrorDisplay
        title={t('error.failedLoading')}
        description={t('error.refresh')}
        className="m-xl min-h-[20em]"
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {t('header.title', 'Exec View')} -{' '}
          {t('title.dataFreshness', 'Data Freshness')}
        </title>
      </Helmet>
      <div className="h-full overflow-y-auto overscroll-contain [overflow-anchor:none]">
        <div className="sticky top-0 z-20 bg-gradient bg-[length:100%] bg-fixed bg-no-repeat px-xl pb-xl pt-lg bg-level-01">
          <h1 className="text-headline-sm font-bold">
            {t('title.dataFreshness', 'Data Freshness')}
          </h1>
        </div>

        <Table
          aria-label={t(
            'dataFreshness.ariaBodyLabel',
            'Data Freshness Table Body',
          )}
          className="w-full table-fixed px-xl"
        >
          <TableHeader className="sticky top-[91px] z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className={cn(
                      'px-sm text-body-sm font-normal first:pl-lg',
                      header.column.columnDef.meta?.className,
                    )}
                  >
                    <div className="absolute -inset-x-xl -bottom-sm -top-md -z-10 block !bg-gradient bg-[length:100%] bg-fixed bg-no-repeat will-change-[border-color] bg-level-01" />
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isFetching ? (
              <SkeletonTable
                size={10}
                headers={table.getHeaderGroups()[0].headers}
              />
            ) : null}

            {!isFetching && table.getRowModel().rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-lg text-center"
                >
                  {t('noResults', 'No results found.')}
                </TableCell>
              </TableRow>
            )}

            {!isFetching &&
              table.getRowModel().rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  className="group/row relative transition-all duration-300 ease-out hover:bg-emphasis-subtle"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        'border-y p-0 transition-all duration-300 ease-out border-transparent',
                        'group-hover/row:border-emphasis-stronger',
                        'first:rounded-l-sm first:border-l',
                        'last:rounded-r-sm last:border-r last:pr-sm',
                        index % 2 === 0 && 'bg-default-inverse/default',
                        'first:pl-lg',
                        cell.column.columnDef.meta?.className,
                      )}
                    >
                      <div className="flex items-center gap-sm py-sm">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

DataFreshness.displayName = 'DataFreshness';

export { DataFreshness };
